import { z } from "zod";

// ---------------------------------------------------------------------------
// Tier / variant / lifecycle
// ---------------------------------------------------------------------------
export type KycTier = 2 | 3;
export type FormVariant = "tier_2_refresh" | "tier_3_refresh";
export type ReviewLifecycleStatus = "none" | "pending";
export type KycReviewState = "current" | "due";

// Tri-state for Yes/No fields that may arrive as boolean, "1"/"0", "yes"/"no",
// or "" before the user answers.
export type TriState = boolean | string | number | "";

// ---------------------------------------------------------------------------
// Editable KYC sub-shapes (client-side draft). Loose by design: the live KYC
// document carries many optional fields and we only edit a known subset.
// ---------------------------------------------------------------------------
export interface KycName {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  suffix?: string;
}

export interface KycCivil {
  civilStatus?: string;
  spouseName?: string;
}

export interface KycAddress {
  street?: string;
  barangay?: string;
  city?: string;
  province?: string;
  zip?: string;
  country?: string;
}

export interface KycIds {
  idType?: string;
  idNumber?: string;
  idValidity?: string;
  hasExpiration?: boolean;
  TIN?: string;
  SSS?: string;
  isPrimaryId?: boolean;
}

export interface KycWork {
  // Legacy records may be a string; current KYC stores [occupation, position].
  primaryOccupation?: string | string[];
  nameOfEmployer?: string;
  businessNature?: string;
  businessNum?: string;
  annualIncomeAmount?: number | string;
  sourceOfFunds?: string;
}

export interface KycContact {
  mailingAddress?: string;
}

export interface KycPep {
  governmentWork?: TriState;
  governmentRelative?: TriState;
  banks?: string;
}

export interface KycFatca {
  usPerson?: TriState;
  usCitizen?: TriState;
  usResident?: TriState;
  usAlien?: TriState;
  usPhone?: TriState;
  usBorn?: TriState;
  usAddressResidence?: TriState;
  usAddressMailing?: TriState;
  usAccount?: TriState;
  usPOA?: TriState;
  usCareOfAddress?: TriState;
}

export interface KycIdImage {
  image: string;
  name?: string;
  url?: string;
  size?: string;
  mimeType?: string;
  fileName?: string;
}

export interface KycData {
  email?: string;
  mobileNumber?: string;
  gender?: string;
  dateOfBirth?: string;
  placeOfBirth?: string;
  cityOfBirth?: string;
  nationality?: string;
  name?: KycName;
  civil?: KycCivil;
  contact?: KycContact;
  currentAddress?: KycAddress;
  permanentAddress?: KycAddress;
  businessAddress?: KycAddress;
  ids?: KycIds;
  work?: KycWork;
  PEP?: KycPep;
  FATCA?: KycFatca;
  idImages?: KycIdImage[];
  [key: string]: unknown;
}

export interface UserData {
  firstName?: string;
  lastName?: string;
  mobileNumber?: string;
  email?: string;
  riskProfile?: string;
  [key: string]: unknown;
}

// riskQuestionnaire groups arrive as { [group]: { [group1|2|3]: boolean } }
// plus a dateRiskFilled string.
export type RiskQuestionnaire = Record<string, unknown>;

export interface AccountDetails {
  accountName?: string;
  bankName?: string;
  bankAccountNumber?: string;
  savingsAccount?: boolean | "";
  [key: string]: unknown;
}

// Metadata returned by POST /v2/kyc-review/image, echoed back verbatim in the
// update payload's `attachments` array.
export interface KycAttachmentMeta {
  image: string;
  name?: string;
  url?: string;
  size?: string;
  mimeType?: string;
  fileName?: string;
}

// ---------------------------------------------------------------------------
// API envelopes (lenient — the backend may add fields; we read a known subset)
// ---------------------------------------------------------------------------
const looseObject = z.record(z.string(), z.unknown());

export const kycReviewStatusSchema = z.object({
  applicable: z.boolean().optional(),
  status: z.enum(["current", "due"]).optional(),
  blocked: z.boolean().optional(),
  lastCompletedAt: z.string().nullable().optional(),
  nextReviewAt: z.string().nullable().optional(),
  latestUpdateId: z.string().nullable().optional(),
});
export type KycReviewStatus = z.infer<typeof kycReviewStatusSchema>;

export const kycReviewCurrentSchema = z.object({
  tier: z.number().nullable().optional(),
  formVariant: z.string().optional(),
  kycReview: kycReviewStatusSchema.optional(),
  review: z
    .object({
      pendingSubmissionId: z.string().nullable().optional(),
      pendingKind: z.string().nullable().optional(),
      status: z.enum(["none", "pending"]).optional(),
    })
    .optional(),
  current: z
    .object({
      user: looseObject.optional(),
      kyc: looseObject.optional(),
      riskQuestionnaire: looseObject.optional(),
      accountDetails: looseObject.optional(),
    })
    .optional(),
});
export type KycReviewCurrent = z.infer<typeof kycReviewCurrentSchema>;

// Safe parse helper — never throws; returns null on shape mismatch so callers
// can fall back to a load error instead of crashing.
export function parseKycReviewCurrent(data: unknown): KycReviewCurrent | null {
  const result = kycReviewCurrentSchema.safeParse(data);
  return result.success ? result.data : null;
}
